import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUrlState } from '../hooks/useUrlState';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import {
  Activity,
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  ClipboardList,
  FileText,
  Loader2,
  PauseCircle,
  Plus,
  Search,
  Trash2,
  X,
} from 'lucide-react';
import { sortCompare, makeToggleSort } from '../components/SortTh';
import { PageHeaderBand } from '../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../components/ui/LoadFailureNotice';
import { LOAD_FAILURE_QUERY_OPTIONS, queryFailureError, queryLoadFailure } from '../lib/load-failure';
import { displayValue } from '../features/fieldiq-handoff/contracts';
import { getJobTypeLabel, jobTypeMatchesSearch, JOB_TYPE_EMPTY_LABEL } from '../constants/job-types';
import { formatCalendarDate, formatScheduledTime } from '../lib/calendar-date';
import { JOB_STATUS_STRIPE, isJobStatus, jobStatusLabel, jobStatusStyle } from '../lib/job-status';
import { useAuth } from '../contexts/AuthContext';
import { isSalesRole } from '../lib/sales-access';

/**
 * The jobs register, on the page layout gold standard.
 *
 * RE-SKIN ONLY. The query, the search predicate, the status filter, every sort
 * key, the deep-link seed off `?status=` and all six navigation targets do
 * exactly what they did. Nothing here fetches anything new: the stat strip and
 * the chip counts are both tallies of the one list this screen already loads.
 *
 * THE TALLY IS HONEST BECAUSE THE LIST IS COMPLETE. `GET /jobs` takes no
 * parameters and applies no LIMIT — it returns every non-deleted job for the
 * company ordered by creation, verified in the API's job-management route, not
 * assumed. That is the Rate Sheets case rather than the Quotes case: there is
 * no endpoint returning these figures, so counting loaded rows is the only
 * answer available and it is not a second one. If a page parameter is ever
 * added to that route, every count on this screen becomes a count of the
 * current page and has to move to the server.
 *
 * COLUMN ORDER IS A CONTRACT. `JobsScreen.date.test.tsx` reaches the scheduled
 * day by cell index, so the eight columns below stay in the order they were in:
 * job number, client, rig/well, type, ticket, date, status, chevron.
 *
 * THE TWO LOCAL STATUS MAPS ARE GONE. They were a bare text colour and a
 * status-tinted job-TYPE chip, both rendered on the narrow-viewport card only,
 * and both carried long docblocks explaining that they were deliberately not
 * converged with the canonical palette. What they were waiting for was a reason
 * to change the card's shape, and the register idiom is one: the card now
 * carries the same solid status pill the wide listing does, and the job-type
 * chip is neutral, because a chip naming a job type has no business being
 * coloured by status while a badge two lines above already names the status.
 * The seven-encoding list in lib/job-status.ts was updated to match.
 */

/**
 * The status chips, and what each one asks of the loaded rows.
 *
 * CHIPS, NOT TABS: every one of these narrows the SAME list of jobs by status,
 * so each carries its count. Nothing here switches which question the page
 * answers.
 *
 * `open` is the four live statuses and is the default, which is why it is first
 * and why it is not simply the absence of a filter. `all` is that absence.
 *
 * THE SET IS EXHAUSTIVE OVER THE ENUM, and it has to be, because the filter is
 * seeded from `?status=` on entry. `on_hold` and `cancelled` had no chip while
 * both were reachable by deep link, so landing on one showed a narrowed list
 * with every chip inactive and nothing on screen saying what had narrowed it —
 * the reader cannot distinguish that from a list which is simply short.
 *
 * THE RULE IS THE URL'S RANGE, NOT THE DESIGNER'S SHORTLIST: whatever
 * `?status=` can hold must be able to light a chip. That is why this list is
 * checked against the status enum by a test rather than left to review — a
 * seventh status added to `lib/job-status.ts` reappears as the same defect, and
 * it reappears silently, because nothing about a missing chip throws.
 *
 * `cancelled` sits last and after `all` deliberately: it is the one terminal
 * state that is not a completion, and grouping it with the live statuses would
 * put it in the run of things a dispatcher is working through.
 */
const FILTERS = [
  { key: 'open', label: 'Open' },
  { key: 'pending', label: 'Pending' },
  { key: 'scheduled', label: 'Scheduled' },
  { key: 'active', label: 'Active' },
  { key: 'on_hold', label: 'On Hold' },
  { key: 'completed', label: 'Completed' },
  { key: 'cancelled', label: 'Cancelled' },
  { key: 'all', label: 'All' },
] as const;

/** The four live statuses `open` selects. One definition, three readers. */
const OPEN_STATUSES = ['pending', 'scheduled', 'active', 'on_hold'];

/**
 * The 4px rail on a row's left edge, keyed on status.
 *
 * IT REUSES THE CANONICAL JOB-STATUS STRIPE and deliberately does NOT call
 * `jobStatusStripe`. That helper's unknown-status arm paints a neutral edge on
 * purpose, because its caller is a dispatch card whose only visible boundary in
 * light mode IS the stripe — losing it there makes the card invisible. A row in
 * this register has its own borders, so the standard applies instead: an
 * unrecognised status keeps the 4px width and claims no colour, because a rail
 * is a claim about which state a row is in and a wrong claim in a colour is the
 * silent kind of wrong.
 */
const statusRail = (status: unknown): string =>
  isJobStatus(status) ? JOB_STATUS_STRIPE[status] : 'border-l-transparent';

/**
 * The status pill.
 *
 * IT IS THE CANONICAL SIX-HUE JOB PALETTE, not the five `--fi-signal-*` fills
 * the reference registers use, and that is a considered departure rather than
 * an oversight. The standard's instruction where an enum outgrows the palette
 * is to share the nearest hue and never to add a colour — and this palette is
 * not a new colour. It already exists, it is already saturated with its own
 * paired ink, it already flips per theme (bright fill with near-black ink in
 * dark, dark fill with white ink in light), and its worst cell across six
 * statuses, six surfaces and both themes measured 4.35 against a 4.5 floor for
 * the label and a 3:1 floor for the pill. lib/job-status.ts carries the matrix.
 *
 * What sharing would cost is the point. The signal palette has no purple and no
 * orange, so six job statuses onto five hues forces either `completed` and
 * `cancelled` together — a finished job and an abandoned one, which are the two
 * a register must never blur — or `scheduled` and `on_hold`, which are opposite
 * ends of the same week. It would also make this screen the only one of the
 * four in this series disagreeing with the dispatch board, the calendar pill
 * and the dashboard, all of which paint these same six.
 *
 * ABSENT AND UNKNOWN GO THROUGH THE SHARED HELPERS, which already give the
 * three outcomes this repo asks for: a known value gets its label, an absent
 * one an em dash, and a value outside the enum is echoed raw on the neutral
 * fill so somebody notices the API grew a status. Neither arm is reachable from
 * the live API — the column is NOT NULL with a six-member enum default — but
 * the fallback must not be a real status either way.
 */
function JobStatusBadge({ status }: { status: unknown }) {
  return (
    <span
      className={`inline-flex w-fit items-center whitespace-nowrap rounded-fi-pill border border-black/20 px-2.5 py-1 text-[0.68rem] font-black uppercase tracking-[0.06em] leading-none ${jobStatusStyle(status)}`}
    >
      {jobStatusLabel(status)}
    </span>
  );
}

/**
 * A sortable heading cell in the handoff palette.
 *
 * LOCAL PAINT, SHARED LOGIC — the same split QuotesScreen documents. The
 * comparator and the toggle are imported from `components/SortTh`, so there is
 * one sort implementation and not two; only the colours differ, because the
 * shared heading is spelled in the older surface names and re-skinning it would
 * repaint the twenty-odd screens still rendering it.
 *
 * Quotes is now the second call site for this paint and said the second one is
 * when the abstraction stops being a guess. Extracting it is owed work and is
 * NOT done here: Quotes is a pinned reference for the standard, and rewriting
 * the reference page inside a change whose subject is a different screen is the
 * drive-by the house rules refuse. It wants its own diff, with both call sites
 * converging where a reviewer can see them.
 */
interface SortHeadProps {
  label: string;
  field: string;
  sortBy: string;
  sortDir: 'asc' | 'desc';
  onSort: (field: string) => void;
  className?: string;
}

function SortHead({ label, field, sortBy, sortDir, onSort, className = '' }: SortHeadProps) {
  const active = sortBy === field;
  return (
    <th
      scope="col"
      aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
      onClick={() => onSort(field)}
      className={`group/th cursor-pointer select-none whitespace-nowrap px-4 py-3 text-left transition hover:text-white ${
        active ? 'text-white' : 'text-fieldiq-muted'
      } ${className}`}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <span className="inline-flex flex-col leading-none">
          <ChevronUp
            className={`size-3 ${
              active && sortDir === 'asc' ? 'text-fi-amber' : 'text-fieldiq-muted/40 group-hover/th:text-fieldiq-muted/60'
            }`}
          />
          <ChevronDown
            className={`-mt-0.5 size-3 ${
              active && sortDir === 'desc' ? 'text-fi-amber' : 'text-fieldiq-muted/40 group-hover/th:text-fieldiq-muted/60'
            }`}
          />
        </span>
      </span>
    </th>
  );
}

/**
 * A job's scheduled date is a CALENDAR DAY ('2026-07-14'), not an instant.
 * `new Date()` parsed it as UTC midnight, so every date in this list rendered a
 * day early in Alberta — a job scheduled for Jul 14 said "Jul 13, 2026".
 *
 * A straight swap, not the fmtDate/fmtInstantDay split TicketDetail needed:
 * both call sites pass `job.scheduled_date || job.scheduledDate`, which is two
 * spellings of one column.
 *
 * THE LINE THAT USED TO END THIS BLOCK WAS "Nothing reaches this helper that
 * has a meaningful time of day", AND IT WAS FALSE. Measured 2026-09-09: 125 of
 * 150 scheduled rows estate-wide carry a real time of day, 34 of TNT's 39. The
 * helper was right anyway — a date-part read is the correct way to get the DAY
 * out of these columns — but the reason given for it was wrong, and the wrong
 * reason is why no screen rendered the hour for as long as this file has
 * existed. Discarding the time here is now a decision, not an assumption:
 * `formatScheduledTime` renders it alongside.
 */
function fmtDate(d: any) {
  return formatCalendarDate(d) ?? '—';
}

export default function JobsScreen() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSales = isSalesRole(user?.role); // sales reads jobs, creates none (api#1032)
  // Held in the URL, not useState, so Back from a job returns to the same
  // search, chip and sort (useUrlState). `status` was already the inbound key.
  const [filter, setFilter] = useUrlState<string>('status', 'open');
  const [search, setSearch] = useUrlState<string>('q', '');
  const [sortBy, setSortBy] = useUrlState<string>('sort', 'scheduled_date');
  const [sortDir, setSortDir] = useUrlState<'asc' | 'desc'>('dir', 'desc');

  /**
   * A failed list is not an empty yard.
   *
   * This query was `{ data, isLoading, isError }`, and the two branches that
   * came out of it could not tell a dead server from a company with no jobs:
   * `isError` is FALSE while React Query is waiting to retry a 5xx, the failure
   * sits on `failureReason` instead, and `?? []` turns the undefined data into
   * an empty list — so a tenant whose API was down read "No jobs found" and, on
   * the All chip with an empty search, was offered "Create your first job to get
   * started" as the remedy for a failed READ.
   *
   * `queryLoadFailure` reads the paused state as well as the settled one, and
   * `retryUnlessRefused` stops a 401/403 being retried, because a refusal is an
   * answer rather than a flake.
   */
  const jobsQuery = useQuery({
    queryKey: ['jobs'],
    queryFn: () => api.jobs.list().then(r => r.data.data ?? []),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });

  const { data, isLoading } = jobsQuery;
  const jobsFailure = queryLoadFailure(jobsQuery);

  const jobs: any[] = data ?? [];

  /**
   * HAS THE LIST ACTUALLY ARRIVED? One flag, three readers — the stat strip,
   * the chip counts and the register's scope line.
   *
   * `isLoading` IS NOT THAT QUESTION, and this was wrong here until the offline
   * fixture said so. A query that has failed and is waiting to retry sits at
   * `status: 'pending'`, `fetchStatus: 'paused'` — and React Query's
   * `isLoading` is pending AND fetching, so while it is paused `isLoading` is
   * FALSE. Every figure gated on it therefore un-masked over `data` that was
   * still `undefined`, `?? []` made that an empty list, and the band rendered
   * `Scheduled 0 · Active 0 · On Hold 0 · Completed 0` with every chip on zero,
   * directly above a panel reading "Couldn't load jobs". Measured in the
   * browser against a forced 500, not reasoned about.
   *
   * That is the em-dash rule one layer up: a zero is a statement about the
   * company, and the only thing that had happened was a failed request. The
   * register got this right on its own because it branches on the failure; the
   * band and the chips did not, because they branched on the spinner.
   *
   * `data !== undefined` rather than `jobs.length` — an empty array is a real
   * answer ("this tenant has no jobs") and has to stay distinguishable from no
   * answer at all.
   */
  const listArrived = !isLoading && !jobsFailure && data !== undefined;

  /* IS IT STILL IN FLIGHT? A DIFFERENT QUESTION FROM "HAS IT ARRIVED", AND THE
   * BAND'S PULSE IS THE ONE THAT NEEDS IT.
   *
   * `!listArrived` is true while the read is in flight AND true forever after it
   * fails, so the flag that draws a pulse where each figure goes could not tell
   * the two apart. A failed read HAS an answer — "we could not read this" — and
   * the band's spelling for it is an em dash. A pulse says the opposite: that a
   * number is coming. So the strip animated indefinitely above a register that
   * had already said "Couldn't load jobs".
   *
   * This is the docblock above, one state further along. There the strip
   * un-masked too early and printed a confident 0; here it never un-masked and
   * printed a confident "not yet". Both let the band claim something about the
   * company from a fact about the network, which is the rule this file already
   * carries twice.
   *
   * THE FIGURES HAD TO MOVE WITH IT, and flipping the flag alone would have
   * restored the fe#729 zeros exactly. `counts` is derived from `jobs`, which is
   * `data ?? []`, so on a failed read every count is a real, honest `0` over an
   * empty list — and `displayValue(0)` is `0`, not a dash. Each value is
   * therefore gated on `listArrived` and passes `null` when the list never came,
   * which is the only spelling `displayValue` turns into an em dash. */
  const listPending = !listArrived && !jobsFailure;

  // Search + filter
  const filtered = useMemo(() => {
    let result = jobs;

    // Text search
    if (search) {
      const s = search.toLowerCase();
      result = result.filter((j: any) => {
        const jobNum = (j.job_number || j.jobNumber || '').toLowerCase();
        const customer = (j.customer_name || j.customerName || '').toLowerCase();
        const rig = (j.rig_name || j.rigName || '').toLowerCase();
        const well = (j.well_name || j.wellName || '').toLowerCase();
        const wellId = (j.well_id_lsd || j.wellIdLsd || '').toLowerCase();
        const location = (j.location_city || j.locationCity || '').toLowerCase();
        // job_type is a canonical code; match the code AND its label so the
        // text on screen ("Liner & Tie-Back") is searchable.
        const jobTypeHit = jobTypeMatchesSearch(j.job_type || j.jobType, search);
        return jobNum.includes(s) || customer.includes(s) || rig.includes(s) || well.includes(s) || wellId.includes(s) || jobTypeHit || location.includes(s);
      });
    }

    // Status filter
    if (filter === 'open') {
      result = result.filter((j: any) => OPEN_STATUSES.includes(j.status));
    } else if (filter !== 'all') {
      result = result.filter((j: any) => j.status === filter);
    }

    return result;
  }, [jobs, filter, search]);

  /**
   * Counts for the chips and figures for the stat strip, from the one loaded
   * list. Both ignore the search box and the chips themselves, exactly as the
   * reference pages' strips do: they describe the company, not the current
   * narrowing, and a count that moved as you typed would be a fourth thing on
   * screen answering the same question as the register's own scope line.
   *
   * A TALLY OVER LOADED ROWS IS A REAL NUMBER, so a status with none of them
   * reads `0` and not a gap. The em dash is reserved for a figure that did not
   * arrive, and while the list is in flight nothing is claimed at all — the
   * chips render no number and the stats pulse.
   */
  const counts = useMemo(() => ({
    all: jobs.length,
    open: jobs.filter((j: any) => OPEN_STATUSES.includes(j.status)).length,
    active: jobs.filter((j: any) => j.status === 'active').length,
    scheduled: jobs.filter((j: any) => j.status === 'scheduled').length,
    pending: jobs.filter((j: any) => j.status === 'pending').length,
    on_hold: jobs.filter((j: any) => j.status === 'on_hold').length,
    completed: jobs.filter((j: any) => j.status === 'completed').length,
    cancelled: jobs.filter((j: any) => j.status === 'cancelled').length,
  }), [jobs]);

  // Sorting
  const statusOrder: Record<string, number> = { pending: 0, scheduled: 1, active: 2, on_hold: 3, completed: 4, cancelled: 5 };
  const toggleSort = makeToggleSort(setSortBy, setSortDir, sortBy);
  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      let aVal: any, bVal: any;
      switch (sortBy) {
        case 'job_number': aVal = a.job_number || a.jobNumber || ''; bVal = b.job_number || b.jobNumber || ''; break;
        case 'customer_name': aVal = a.customer_name || a.customerName || ''; bVal = b.customer_name || b.customerName || ''; break;
        // SORT ON WHAT THE CELL SHOWS. The Rig / Well cell renders
        // `rigName || wellName || '—'`, so a job with no rig but a well
        // displays the well and sorted as an empty string — it sank to one end
        // of the list under a heading that appeared to be about it. The
        // fallback chain here is the cell's, character for character; if one
        // moves the other has to.
        case 'rig_name': aVal = a.rig_name || a.rigName || a.well_name || a.wellName || ''; bVal = b.rig_name || b.rigName || b.well_name || b.wellName || ''; break;
        case 'job_type': aVal = a.job_type || a.jobType || ''; bVal = b.job_type || b.jobType || ''; break;
        case 'scheduled_date': aVal = a.scheduled_date || a.scheduledDate || ''; bVal = b.scheduled_date || b.scheduledDate || ''; break;
        case 'status': aVal = statusOrder[a.status] ?? 99; bVal = statusOrder[b.status] ?? 99; break;
        default: return 0;
      }
      return sortCompare(aVal, bVal, sortDir);
    });
  }, [filtered, sortBy, sortDir]);

  const openJob = (id: string) => navigate(`/jobs/${id}`);

  /**
   * Enter and Space open a row — but ONLY when the row itself has focus.
   *
   * This register is the Rate Sheets case, not the Quotes case: a row carries
   * live controls of its own (a button per ticket on the job). So the row takes
   * no explicit button role, because that role makes its children presentational
   * and stops a screen reader announcing those tickets at all; and the guard is
   * what stops a key aimed at a ticket being swallowed by the row and opening
   * the job instead.
   */
  const rowKeys = (id: string) => (event: React.KeyboardEvent) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openJob(id);
    }
  };

  return (
    <div data-testid="jobs-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      {/* The app shell renders the one global branded bar. Pages never do. */}
      <PageHeaderBand
        eyebrow="Casing operations"
        title="Jobs"
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={
          <>
            <button
              type="button"
              onClick={() => navigate('/jobs/trash')}
              className="fi-action fi-action--standard border border-white/25 hover:border-white/60"
            >
              <Trash2 className="size-4" aria-hidden="true" />
              Deleted Jobs
            </button>
            {!isSales && (
            <button
              type="button"
              onClick={() => navigate('/jobs/new')}
              className="fi-action fi-action--standard fi-action--primary"
            >
              <Plus className="size-4" aria-hidden="true" />
              New Job
            </button>
            )}
          </>
        }
        /* Four figures, all of them tallies of the list already loaded above —
           no second query, and none of them recomputed from anything the API
           states directly. They are the status distribution in lifecycle order,
           company-wide, and they do NOT follow the chips or the search box.

           THE THIRD LABEL IS SEVEN CHARACTERS ON PURPOSE. The hero circle's
           edge crosses the third stat column between 1024 and 1048 wide, and
           the labels that have been swept there run to about 70px. "On Hold"
           sits inside that; a longer word in this slot needs the sweep re-run,
           not a judgement call.

           `Completed` is every completed job the tenant has ever had, which is
           why its sub-line says so rather than leaving the reader to assume a
           period. */
        stats={[
          {
            icon: <CalendarDays className="size-4" aria-hidden="true" />,
            label: 'Scheduled',
            sub: 'Booked, not started',
            tone: 'info',
            loading: listPending,
            value: displayValue(listArrived ? counts.scheduled : null),
          },
          {
            icon: <Activity className="size-4" aria-hidden="true" />,
            label: 'Active',
            sub: 'Running now',
            tone: 'success',
            loading: listPending,
            value: displayValue(listArrived ? counts.active : null),
          },
          {
            icon: <PauseCircle className="size-4" aria-hidden="true" />,
            label: 'On Hold',
            sub: 'Paused',
            tone: 'accent',
            loading: listPending,
            value: displayValue(listArrived ? counts.on_hold : null),
          },
          {
            icon: <CheckCircle2 className="size-4" aria-hidden="true" />,
            label: 'Completed',
            sub: 'All time',
            tone: 'neutral',
            loading: listPending,
            value: displayValue(listArrived ? counts.completed : null),
          },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {/* Chips, search and the narrow-viewport sort — in the body, never on
              the band. */}
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
            <div className="scrollbar-hide flex flex-1 gap-2 overflow-x-auto">
              {FILTERS.map((f) => {
                const active = filter === f.key;
                return (
                  <button
                    key={f.key}
                    type="button"
                    /* The active chip is amber-filled, and that was the ONLY
                       carrier of the state — a colour, which a screen reader
                       does not read and a test can only assert as a class
                       string. The ticket register's chips already carry this;
                       the two rows are the same control and now say so. */
                    aria-pressed={active}
                    onClick={() => setFilter(f.key)}
                    className={`inline-flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-fi-pill px-4 py-1.5 text-xs font-extrabold transition ${
                      active
                        ? 'bg-fi-amber text-fi-amber-ink'
                        : 'border border-fieldiq-border bg-fieldiq-card text-fieldiq-muted hover:text-fieldiq-text'
                    }`}
                  >
                    {f.label}
                    {/* No count is claimed while the list is in flight, and a
                        real zero is shown once it has arrived — a chip that
                        hides its own zero cannot be told apart from one whose
                        count never loaded. Full ink on the active chip: the
                        70% ink the first re-skins used measures 3.7 on amber,
                        under the floor. */}
                    {listArrived && (
                      <span className={`tabular-nums ${active ? 'text-fi-amber-ink' : 'text-fieldiq-muted'}`}>
                        {displayValue((counts as any)[f.key])}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            <div className="flex items-center gap-3 lg:w-96">
              <div className="relative flex-1">
                <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-fieldiq-muted" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by client, job #, well, rig, type..."
                  aria-label="Search jobs"
                  className="w-full rounded-fi-control border border-fieldiq-border bg-fieldiq-card py-1.5 pl-10 pr-8 text-sm text-white placeholder:text-fieldiq-muted focus:border-fi-amber focus:outline-none"
                />
                {search && (
                  <button
                    type="button"
                    onClick={() => setSearch('')}
                    aria-label="Clear search"
                    className="absolute right-2.5 top-1/2 -translate-y-1/2"
                  >
                    <X className="size-4 text-fieldiq-muted" />
                  </button>
                )}
              </div>
              {/* The narrow-viewport sort. It sets ascending on every pick,
                  which is NOT what the heading toggle does, and that difference
                  is pre-existing: there is no heading row to toggle down here,
                  so a second press has nothing to reverse. */}
              <select
                value={sortBy}
                onChange={(e) => { setSortBy(e.target.value); setSortDir('asc'); }}
                aria-label="Sort jobs"
                className="flex-shrink-0 rounded-fi-control border border-fieldiq-border bg-fieldiq-card px-3 py-1.5 text-xs text-white md:hidden"
              >
                <option value="scheduled_date">Newest</option>
                <option value="job_number">Job #</option>
                <option value="status">Status</option>
                <option value="customer_name">Customer</option>
              </select>
            </div>
          </div>

          <section
            data-testid="job-register"
            aria-labelledby="job-register-title"
            className="overflow-hidden rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card"
          >
            {/* The header line carries SCOPE — how much of the company's work
                this listing is currently showing. That is something the reader
                would otherwise get wrong after a search, which is what earns a
                figure a place here. It is withheld until the list has arrived,
                so a failed load cannot present as "0 of 0". */}
            <header className="flex items-end justify-between gap-3 border-b border-fieldiq-border px-4 py-4">
              <div>
                <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Job register</p>
                <h2 id="job-register-title" className="mt-1 text-base font-extrabold tracking-tight text-white">
                  Scheduled and running work
                </h2>
              </div>
              {listArrived && (
                <p className="text-sm tabular-nums text-fieldiq-muted">
                  {sorted.length} of {jobs.length} job{jobs.length !== 1 ? 's' : ''}
                </p>
              )}
            </header>

            {isLoading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="size-6 animate-spin text-fieldiq-muted" />
              </div>
            ) : jobsFailure ? (
              /* The third branch, where the empty state would have been. No
                 `allowed` is passed: `GET /jobs` carries authentication and no
                 role gate, so naming who may read it would be a guess. */
              <LoadFailureNotice
                error={queryFailureError(jobsQuery)}
                kind={jobsFailure}
                subject="jobs"
              />
            ) : sorted.length === 0 ? (
              <div className="px-4 py-12 text-center">
                <ClipboardList className="mx-auto mb-3 size-10 text-fieldiq-muted" aria-hidden="true" />
                <p className="text-sm text-fieldiq-text">No jobs found.</p>
                <p className="mt-1 text-xs text-fieldiq-muted">
                  {filter === 'all' && !search
                    ? 'Create your first job to get started.'
                    : 'Try adjusting your search or filter.'}
                </p>
              </div>
            ) : (
              <>
                {/* Wide viewports: the register as a tabular listing. */}
                <div className="hidden overflow-x-auto md:block">
                  <table className="w-full min-w-[980px] text-left">
                    <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40 text-[0.64rem] font-black uppercase tracking-[0.12em]">
                      {/* The heading row carries the same 4px on its left edge
                          in no colour at all, so the columns line up with the
                          values under them. */}
                      <tr className="border-l-4 border-l-transparent">
                        <SortHead label="Job #" field="job_number" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} className="w-[110px]" />
                        <SortHead label="Client" field="customer_name" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                        <SortHead label="Rig / Well" field="rig_name" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                        <SortHead label="Type" field="job_type" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                        <th scope="col" className="whitespace-nowrap px-4 py-3 text-fieldiq-muted">Ticket #</th>
                        <SortHead label="Date" field="scheduled_date" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                        <SortHead label="Status" field="status" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                        <th scope="col" className="w-[70px] px-4 py-3" />
                      </tr>
                    </thead>
                    <tbody>
                      {sorted.map((job: any) => {
                        const jobNum = job.job_number || job.jobNumber;
                        const status = job.status;
                        const customerName = job.customer_name || job.customerName || '—';
                        const rigName = job.rig_name || job.rigName || '';
                        const wellName = job.well_name || job.wellName || '';
                        const wellIdLsd = job.well_id_lsd || job.wellIdLsd || '';
                        const jobType = job.job_type || job.jobType || '';
                        const scheduledDate = job.scheduled_date || job.scheduledDate;
                        const tickets = Array.isArray(job.tickets) ? job.tickets : [];

                        return (
                          <tr
                            key={job.id}
                            tabIndex={0}
                            onClick={() => openJob(job.id)}
                            onKeyDown={rowKeys(job.id)}
                            className={`cursor-pointer border-t border-l-4 border-fieldiq-border outline-none transition hover:bg-fieldiq-dark/50 focus-visible:bg-fi-amber/10 ${statusRail(status)}`}
                          >
                            <td className="whitespace-nowrap px-4 py-3 text-sm font-extrabold text-fi-amber">{jobNum}</td>
                            <td className="px-4 py-3 text-sm font-bold text-white">{customerName}</td>
                            <td className="px-4 py-3">
                              <div className="text-sm text-white">{rigName || wellName || '—'}</div>
                              {wellIdLsd && <div className="text-xs text-fieldiq-muted">{wellIdLsd}</div>}
                            </td>
                            <td className="px-4 py-3">
                              {/* A job-TYPE chip, and neutral on purpose: the
                                  status badge two columns along already names
                                  the status, and a second control tinted by it
                                  is a weaker copy of that answer. */}
                              {jobType ? (
                                <span className="whitespace-nowrap rounded-fi-control bg-fieldiq-dark px-2.5 py-1 text-xs font-medium text-fieldiq-text">
                                  {getJobTypeLabel(jobType)}
                                </span>
                              ) : (
                                <span className="text-sm text-fieldiq-muted">{JOB_TYPE_EMPTY_LABEL}</span>
                              )}
                            </td>
                            {/* The tickets on this job, each its own control
                                into the ticket it names. They were spans with a
                                click handler, which no keyboard could reach;
                                as buttons they are focusable, which is exactly
                                why the row above takes the guarded keydown and
                                no explicit button role. */}
                            <td className="px-4 py-3 text-sm">
                              {tickets.length > 0 ? (
                                <div className="flex flex-wrap gap-1.5">
                                  {tickets.map((t: any) => (
                                    <button
                                      key={t.id}
                                      type="button"
                                      /* `/tickets/${id}`, not `/tickets?id=${id}`.
                                         TicketsScreen reads `jobId` and `status`
                                         off the query string and has never read
                                         `id`, so the old link landed on the
                                         unfiltered register — the right screen,
                                         the wrong row, and no error anywhere.
                                         `/tickets/:ticketId` is the detail route. */
                                      onClick={(e) => { e.stopPropagation(); navigate(`/tickets/${t.id}`); }}
                                      className="rounded-fi-control text-xs font-bold text-fi-amber underline-offset-2 outline-none hover:underline focus-visible:underline"
                                    >
                                      {t.ticket_number}
                                    </button>
                                  ))}
                                </div>
                              ) : (
                                <span className="text-fieldiq-muted">—</span>
                              )}
                            </td>
                            {/*
                              The time is a second line, and it is OMITTED
                              rather than dashed when absent. A dash belongs
                              where there is a labelled slot expecting a value —
                              JobDetail's field — not under a "Date" column,
                              where a dash on every timeless row is noise that
                              hides the rows that do carry an hour. Nothing
                              plausible is being substituted either way.
                            */}
                            <td className="px-4 py-3 text-sm text-fieldiq-text">
                              <div>{fmtDate(scheduledDate)}</div>
                              {formatScheduledTime(scheduledDate) && (
                                <div className="text-xs text-fieldiq-muted">{formatScheduledTime(scheduledDate)}</div>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <JobStatusBadge status={status} />
                            </td>
                            <td className="px-4 py-3">
                              <ChevronRight className="size-4 text-fieldiq-muted" aria-hidden="true" />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Narrow viewports: the same rows, stacked. No status rail
                    here — that 4px edge belongs to the surrounding panel. */}
                <div className="space-y-3 p-4 md:hidden">
                  {sorted.map((job: any) => {
                    const jobNum = job.job_number || job.jobNumber;
                    const status = job.status;
                    const customerName = job.customer_name || job.customerName;
                    const rigName = job.rig_name || job.rigName;
                    const rigNumber = job.rig_number || job.rigNumber;
                    const wellName = job.well_name || job.wellName;
                    const wellIdLsd = job.well_id_lsd || job.wellIdLsd;
                    const serviceType = job.service_type || job.serviceType;
                    const jobType = job.job_type || job.jobType;
                    const scheduledDate = job.scheduled_date || job.scheduledDate;
                    const locationCity = job.location_city || job.locationCity || '';

                    return (
                      <div
                        key={job.id}
                        tabIndex={0}
                        onClick={() => openJob(job.id)}
                        onKeyDown={rowKeys(job.id)}
                        className="cursor-pointer rounded-fi-panel border border-fieldiq-border bg-fieldiq-card outline-none transition hover:border-fieldiq-muted focus-visible:border-fi-amber focus-visible:bg-fi-amber/10"
                      >
                        <div className="flex items-start justify-between gap-3 px-4 pb-3 pt-4">
                          <div className="min-w-0 flex-1">
                            <div className="mb-1.5 flex items-center gap-2">
                              <span className="text-sm font-extrabold text-fi-amber">{jobNum}</span>
                              <JobStatusBadge status={status} />
                            </div>
                            <h3 className="truncate font-display text-base font-extrabold tracking-tight text-white">
                              {customerName || 'No Customer'}
                            </h3>
                            {(rigName || wellName || wellIdLsd) && (
                              <p className="mt-0.5 truncate text-sm text-fieldiq-text">
                                {rigName ? (rigName + (rigNumber ? ` ${rigNumber}` : '')) : wellName}
                                {wellIdLsd ? ` · ${wellIdLsd}` : ''}
                              </p>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); openJob(job.id); }}
                            aria-label={`Open job ${jobNum}`}
                            className="flex-shrink-0 rounded-fi-control p-2 text-fieldiq-muted outline-none transition hover:bg-fieldiq-dark hover:text-fieldiq-text focus-visible:bg-fi-amber/10"
                          >
                            <FileText className="size-4" aria-hidden="true" />
                          </button>
                        </div>
                        <div className="flex flex-wrap items-center gap-2 border-t border-fieldiq-border px-4 py-3">
                          {(serviceType || jobType) && (
                            <span className="rounded-fi-control bg-fieldiq-dark px-2.5 py-1 text-xs font-medium text-fieldiq-text">
                              {/* serviceType is jobs.service_type — free text ("Hot Shot"), NOT job_type. Never mapped. */}
                              {serviceType || getJobTypeLabel(jobType)}
                            </span>
                          )}
                          {locationCity && (
                            <span className="text-sm text-fieldiq-muted">{locationCity}</span>
                          )}
                          {scheduledDate && (
                            <span className="text-sm text-fieldiq-muted">
                              {fmtDate(scheduledDate)}
                              {formatScheduledTime(scheduledDate) ? ` ${formatScheduledTime(scheduledDate)}` : ''}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </section>
        </div>
      </section>

      {/* The one-touch create, kept from before the re-skin.
          `right-4`, not the offset the first re-skinned pages carry: those were
          measured to clear a viewport-pinned agent button that no longer exists
          (fe#680), and nothing occupies that strip now. */}
      {!isSales && (
      <button
        type="button"
        onClick={() => navigate('/jobs/new')}
        aria-label="New job"
        className="fixed bottom-[var(--fab-bottom)] right-4 z-40 grid size-14 place-items-center rounded-fi-pill bg-fi-amber text-fi-amber-ink shadow-lg transition hover:brightness-105 active:scale-95"
      >
        <Plus className="size-6" aria-hidden="true" />
      </button>
      )}
    </div>
  );
}
